import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { firstValueFrom, forkJoin, from, map, of, Subscription } from "rxjs";
import { catchError, finalize, mergeMap } from "rxjs/operators";

import { ETRAINING_DB } from "src/app/app.module";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { DraftItem, DraftLike, FormDataOptions, FormParams, PaginationDetails } from "src/app/models/models";
import { GoogleAuthService } from "src/app/services/google-api.service";
import { setReturnUrl } from "src/app/services/return-url.utils";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { isAuthFailure } from "src/app/shared/utils/auth-session-policy.utils";
import { assignWindowLocation } from "src/app/shared/utils/browser-navigation.utils";
import { isFormTypeVisibleToRole } from "src/app/shared/utils/form-types-visible.utils";
import { toHttpErrorPayload } from "src/app/shared/utils/http-error-message";
import { defaultListDateRange, isWithinListDateFilter } from "src/app/shared/utils/list-date-filter.utils";
import { loginPathWithSessionUpgrade } from "src/session.config";
import { ContainerRatingsService } from "../ratings/container-ratings-form/services/base/container-ratings.services";
import {
	mergeDraftFrontendFieldsFromLocal,
	normalizeStagingApiEntryToDraft,
} from "../ratings/container-ratings-form/shared-v2/utils/draft-frontend-fields.utils";
import { hydrateFormDetailsConfigVersion } from "../ratings/container-ratings-form/shared-v2/utils/form-config-version.utils";
import {
	collectLocalOnlyDraftsToPreserve,
	collectStaleSyncedDraftIdsToRemove,
	resolveStagingPullConflict,
} from "../ratings/container-ratings-form/shared-v2/utils/staging-pull-merge.utils";

@Component({
	selector: "app-pending",
	templateUrl: "./pending.component.html",
	styleUrls: ["./pending.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PendingComponent implements OnInit, OnDestroy {
	constructor(
		private sharedService: SharedService,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private roleService: RoleService,
		private dbService: NgxIndexedDBService, // cbtaDB
		@Inject(ETRAINING_DB) private eTrainingDb: NgxIndexedDBService, // eTrainingDB
		private dialog: MatDialog,
		private drafts: ContainerRatingsService,
		private googleAuth: GoogleAuthService,
	) {}

	formsSub!: Subscription;
	private syncDeletedSub?: Subscription;
	private onlineSub?: Subscription;
	private loadingForms = false;
	forms: DraftLike[] = [];
	private allDrafts: DraftLike[] = [];
	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	dateFilterEnabled = false;
	isOnline: boolean = navigator.onLine;

	public columns: Columns[] = [];
	public configuration!: Config;

	filters?: FormParams = {
		page: CONSTANT.PAGINATION.DEFAULT_PAGE,
		size: CONSTANT.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: CONSTANT.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	@ViewChild("trainingNameTpl", { static: true })
	trainingNameTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("sessionTpl", { static: true }) sessionTpl!: TemplateRef<any>;

	async ngOnInit(): Promise<void> {
		this.bindOnlineStatus();
		this.initTableConfig();
		this.sharedService.setActivePage(RouteType.PENDING);
		this.initFormList();

		if (!this.canReachServer) {
			return;
		}

		if (!(await this.googleAuth.revalidateSession())) {
			this.redirectToLogin();
			return;
		}

		await this.drafts.syncPendingDrafts();
		this.syncDeletedDrafts();
	}

	private bindOnlineStatus(): void {
		this.isOnline = navigator.onLine;
		this.onlineSub?.unsubscribe();
		this.onlineSub = this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});
	}

	/** Shared online flag and the browser must both say the network is up. */
	private get canReachServer(): boolean {
		return this.isOnline && navigator.onLine;
	}

	// Return a Promise and await the Observable
	async getAllStagingDraft(): Promise<void> {
		if (!this.canReachServer) {
			this.initFormList();
			return;
		}

		try {
			const raw: any = await firstValueFrom(
				this.httpService.getFormInStaging({ role: this.roleService.getUserRole() }) as any,
			);

			// Normalize: server may return an array or { data: [...] }
			const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

			// Snapshot local drafts before clear — preserve unsynced/local-only + conflict resolution.
			const existingDrafts = await firstValueFrom(this.eTrainingDb.getAll<DraftItem>(CONSTANT.INDEXEDDB.DRAFT));
			const localDraftById = new Map((existingDrafts ?? []).map((draft) => [draft.id, draft]));

			const serverIds = new Set<string>();
			const draftsToWrite: DraftItem[] = [];
			const draftsToSync: DraftItem[] = [];

			for (const entry of list) {
				const normalized = normalizeStagingApiEntryToDraft(entry);
				if (!normalized?.id) {
					console.warn("Skipping item without id:", entry);
					continue;
				}

				const id = normalized.id;
				serverIds.add(id);
				const localDraft = localDraftById.get(id);
				const serverDraft = normalized;

				// Decide before hydrating — local-newer drafts must survive hydration failures.
				const decision = resolveStagingPullConflict(serverDraft, localDraft);
				if (decision.action === "keep_local") {
					draftsToWrite.push(decision.draft);
					if (decision.shouldSync) {
						draftsToSync.push(decision.draft);
					}
					continue;
				}

				let hydratedServer = serverDraft;
				const formId = hydratedServer.trainingInformation?.formId ?? hydratedServer.formDetails?.id;
				if (hydratedServer.formDetails && formId) {
					hydratedServer = {
						...hydratedServer,
						formDetails: await hydrateFormDetailsConfigVersion(
							hydratedServer.formDetails,
							formId,
							this.eTrainingDb,
							this.httpService,
						),
					};
				}

				draftsToWrite.push(
					mergeDraftFrontendFieldsFromLocal(
						{
							...hydratedServer,
							synced: true,
						},
						localDraft,
					),
				);
			}

			// Preserve unsynced / soft-deleted drafts that never made it to the server list.
			for (const localOnly of collectLocalOnlyDraftsToPreserve(existingDrafts ?? [], serverIds)) {
				draftsToWrite.push(localOnly);
			}

			// Merge server drafts + preserved local-only drafts (no wipe-first).
			const draftsToWriteIds = new Set(draftsToWrite.map((d) => d.id));
			const staleIds = collectStaleSyncedDraftIdsToRemove(existingDrafts ?? [], draftsToWriteIds).filter((id) => {
				const draft = localDraftById.get(id);
				// The pull is role-filtered, so a synced draft for another role is absent
				// from this response even while it is still in progress on the server.
				return !!draft && this.isDraftVisibleToRole(draft as DraftLike);
			});

			for (const id of staleIds) {
				await firstValueFrom(this.eTrainingDb.delete(CONSTANT.INDEXEDDB.DRAFT, id));
			}

			for (const draft of draftsToWrite) {
				await firstValueFrom(this.eTrainingDb.update(CONSTANT.INDEXEDDB.DRAFT, draft));
			}

			// Push local drafts that won timestamp conflict and are still unsynced
			for (const draft of draftsToSync) {
				try {
					await this.drafts.syncOneDraft(draft);
				} catch (syncErr) {
					console.error("Failed to sync newer local draft after staging pull:", draft.id, syncErr);
				}
			}

			this.initTableConfig();
		} catch (err) {
			console.error("getAllStagingDraft error", err);
			const payload = toHttpErrorPayload(err);
			if (isAuthFailure(payload)) {
				const valid = await this.googleAuth.revalidateSession();
				if (!valid) {
					this.redirectToLogin();
				}
			}
		} finally {
			this.initFormList();
		}
	}

	private redirectToLogin(): void {
		setReturnUrl(this.router.url);
		assignWindowLocation(loginPathWithSessionUpgrade(RouteType.LOGIN));
	}

	ngOnDestroy(): void {
		this.formsSub?.unsubscribe();
		this.syncDeletedSub?.unsubscribe();
		this.onlineSub?.unsubscribe();
	}

	// ================= Table config & listing =================
	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = CONSTANT.PAGINATION.MAX_PAGINATION_COUNT;
		this.configuration.rows = CONSTANT.PAGINATION.DEFAULT_SIZE;

		this.columns = [
			{ key: "name", title: "Test Name", cellTemplate: this.trainingNameTpl, width: "35%" },
			{ key: "sessionConfig", title: "Session Details", cellTemplate: this.sessionTpl, width: "25%" },
			{ key: "updatedDate", title: "Last Updated", cellTemplate: this.dateTpl, width: "25%" },
			{ key: "", title: "Action", cellTemplate: this.actionTpl, width: "15%" },
		];
	}

	initFormList(): void {
		if (this.loadingForms) {
			return;
		}

		this.loadingForms = true;
		this.formsSub?.unsubscribe();
		this.loaderService.setLoading(true);

		this.formsSub = this.listAllDrafts()
			.pipe(
				finalize(() => {
					this.loadingForms = false;
					this.loaderService.setLoading(false);
				}),
			)
			.subscribe({
				next: (result) => {
					this.allDrafts = result
						.filter((r: DraftLike) => !r.deleted)
						.filter((r: DraftLike) => this.isDraftVisibleToRole(r));
					this.applyDateFilter();
				},
				error: (error) => {
					console.error(`Error fetching drafts:`, error);
				},
			});
	}

	private resolveDraftFormTypeCode(draft: DraftLike): string {
		const formType = draft.formDetails?.formType ?? draft.trainingInformation?.formType;
		if (typeof formType === "string") {
			return formType;
		}
		return formType?.code ?? "";
	}

	private isDraftVisibleToRole(draft: DraftLike): boolean {
		const formTypeCode = this.resolveDraftFormTypeCode(draft);
		if (!formTypeCode) {
			return false;
		}
		return isFormTypeVisibleToRole(formTypeCode, this.roleService.getUserRole());
	}

	private listAllDrafts() {
		const cbta$ = this.dbService.getAll<DraftLike>(CONSTANT.INDEXEDDB.DRAFT);
		const et$ = this.eTrainingDb.getAll<DraftLike>(CONSTANT.INDEXEDDB.DRAFT);

		return forkJoin([cbta$, et$]).pipe(
			map(([cbtaDrafts, eTrainingDrafts]) => {
				const byId = new Map<string, DraftLike>();

				// 1) Insert eTraining first (authoritative, no legacy badge)
				for (const d of eTrainingDrafts || []) {
					byId.set(d.id, { ...d, __legacy: false, __source: "etraining" });
				}

				// 2) Insert cbta only if not present in eTraining; tag as legacy
				for (const d of cbtaDrafts || []) {
					if (!byId.has(d.id)) {
						byId.set(d.id, { ...d, __legacy: true, __source: "cbta" });
					}
				}

				const unique = Array.from(byId.values());
				return unique.sort((a, b) => new Date(b.lastUpdatedDate).valueOf() - new Date(a.lastUpdatedDate).valueOf());
			}),
		);
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const { page, limit } = event.value;
				this.filters = {
					...this.filters,
					page,
					size: limit,
				};
				this.configuration.rows = limit;
				this.pagination = {
					limit,
					offset: page,
					count: this.forms.length,
				};
				this.updateUrl();
				return;
			default:
				break;
		}
	}

	updateFilters(filters: FormParams) {
		this.filters = filters;

		if (!isEmpty(filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}

		this.updateUrl();

		if (!this.loadingForms) {
			this.initFormList();
		}
	}

	updateUrl(): void {
		const url = this.router.serializeUrl(this.router.createUrlTree([RouteType.PENDING], { queryParams: this.filters }));
		// this.location.go(url);
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? CONSTANT.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? CONSTANT.PAGINATION.DEFAULT_SIZE}`);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;
		return { totalPages, lastPageSize };
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
		} else {
			return;
		}

		this.applyDateFilter();
	}

	private applyDateFilter(): void {
		this.forms = this.allDrafts.filter((row) =>
			isWithinListDateFilter(row.lastUpdatedDate, this.dateFilterEnabled, this.startDate, this.endDate),
		);
		this.pagination = {
			limit: this.size,
			offset: this.page,
			count: this.forms.length,
		};
	}

	formatDate(dateString: string): string {
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	getSessionConfig(sessionConfig: FormDataOptions[]) {
		return sessionConfig?.map((d) => d.description)?.join(", ");
	}

	goToRatingForm(rowId: string, draftId: string): void {
		this.router.navigate([RouteType.RATINGS, rowId, "draft", draftId]);
	}

	// ================= Delete helpers =================

	/** Resolve the proper server id to pass to deleteFormInStaging. */
	private resolveServerId(d: DraftLike | undefined, fallbackId: string): string {
		return d?.trainingAssessmentId || d?.traineeAssessmentId || fallbackId;
	}

	/** Delete from both IndexedDB stores. */
	private deleteLocalDrafts(draftId: string) {
		const delCbta$ = this.dbService.delete(CONSTANT.INDEXEDDB.DRAFT, draftId).pipe(
			catchError((err) => {
				console.warn("cbtaDB delete failed", err);
				return of(null);
			}),
		);
		const delEtraining$ = this.eTrainingDb.delete(CONSTANT.INDEXEDDB.DRAFT, draftId).pipe(
			catchError((err) => {
				console.warn("eTrainingDB delete failed", err);
				return of(null);
			}),
		);

		return forkJoin([delCbta$, delEtraining$]);
	}

	/** Mark as deleted:true in both stores (if present in each store). */
	private markDraftAsDeleted(draftId: string) {
		const flagIn = (store: NgxIndexedDBService) =>
			store.getByKey<DraftLike>(CONSTANT.INDEXEDDB.DRAFT, draftId).pipe(
				mergeMap((item) => {
					if (!item) return of(null);
					const updated: DraftLike = {
						...item,
						deleted: true,
						lastUpdatedDate: new Date().toISOString(),
					};
					return store.update(CONSTANT.INDEXEDDB.DRAFT, updated);
				}),
				catchError((err) => {
					console.warn("flag deleted failed", err);
					return of(null);
				}),
			);

		return forkJoin([flagIn(this.dbService), flagIn(this.eTrainingDb)]);
	}

	/** Delete on server (ignore 404). */
	private deleteOnServer(trainingAssessmentId: string) {
		return this.httpService.deleteFormInStaging(trainingAssessmentId).pipe(
			catchError((err) => {
				if (err?.status === 404) {
					console.info("Staging delete: 404 (already deleted) – ignoring.");
					return of(null);
				}
				console.error("Staging delete failed:", err);
				return of(null);
			}),
		);
	}

	// ================= Delete button (with your constraint) =================

	/**
	 * If ONLINE:
	 *   - call API delete then remove from both stores.
	 * If OFFLINE:
	 *   - ONLY IF the draft exists in eTrainingDB: set deleted:true (in both stores) so it's hidden and queued.
	 *   - ELSE (cbta-only draft): delete locally (best-effort).
	 */
	deletePendingSession(draftId: string) {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "delete-draft-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Delete",
				modalContent: "Do you want to delete this draft?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (!result?.close) {
				sub.unsubscribe();
				return;
			}

			this.loaderService.setLoading(true);

			this.listAllDrafts()
				.pipe(
					map((all) => all.find((x) => x.id === draftId) as DraftLike | undefined),
					mergeMap((draft) => {
						if (this.canReachServer) {
							const serverId = this.resolveServerId(draft, draftId);
							return forkJoin([this.deleteOnServer(serverId), this.deleteLocalDrafts(draftId)]);
						}

						// OFFLINE → only soft-delete if present in eTrainingDB; otherwise hard delete locally
						return this.eTrainingDb.getByKey<DraftLike>(CONSTANT.INDEXEDDB.DRAFT, draftId).pipe(
							mergeMap((eTrainingCopy) => {
								if (eTrainingCopy) {
									return this.markDraftAsDeleted(draftId);
								}
								return this.deleteLocalDrafts(draftId);
							}),
						);
					}),
					finalize(() => {
						this.loaderService.setLoading(false);
						this.initFormList();
					}),
				)
				.subscribe({
					next: () => {},
					error: (e) => console.error("Delete flow error:", e),
				});

			sub.unsubscribe();
		});
	}

	// ================= Sync deleted:true when online =================

	/**
	 * Push drafts marked deleted:true (eTraining ones) to server, then remove them locally.
	 */
	private syncDeletedDrafts() {
		this.syncDeletedSub?.unsubscribe();
		this.syncDeletedSub = this.listAllDrafts()
			.pipe(
				map((all) => (all as DraftLike[]).filter((d) => d.deleted === true)),
				mergeMap((deletedOnes) => {
					if (!deletedOnes.length) return of(null);

					return from(deletedOnes).pipe(
						mergeMap(
							(d) =>
								this.eTrainingDb.getByKey<DraftLike>(CONSTANT.INDEXEDDB.DRAFT, d.id).pipe(
									mergeMap((existsInET) => {
										if (!existsInET) {
											// If somehow flagged but not in eTraining, just clean up locally.
											return this.deleteLocalDrafts(d.id);
										}
										const serverId = this.resolveServerId(d, d.id);
										return this.deleteOnServer(serverId).pipe(
											mergeMap(() => this.deleteLocalDrafts(d.id)),
											catchError((err) => {
												console.warn("Sync delete failed for", d.id, err);
												return of(null);
											}),
										);
									}),
								),
							2,
						), // concurrency
					);
				}),
			)
			.subscribe({
				next: () => {
					this.getAllStagingDraft();
				},
				error: (e) => console.error("syncDeletedDrafts error:", e),
			});
	}
}
